import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { apiGet, apiPostJSON } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './BcpMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

/* 現在地用の別マーカー（避難所の青ピンと区別する赤い丸） */
const currentLocationIcon = L.divIcon({
  className: 'bcp-current-location-icon',
  html: '<div style="background:#e53935;width:18px;height:18px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 2px #e53935;"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

/* 再検索時に地図の中心を更新するコンポーネント */
const MapMover = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 14);
  }, [center, map]);
  return null;
};

const BcpNearbyShelter = () => {
  const [loc, setLoc]             = useState(null);
  const [shelters, setShelters]   = useState([]);
  const [selected, setSelected]   = useState(null);
  const [msg, setMsg]             = useState('');
  const [savedMsg, setSavedMsg]   = useState('');
  const [searching, setSearching] = useState(false);
  const [loadingMap, setLoadingMap] = useState(false);
  const [dataSource, setDataSource] = useState(null); // 'db' | 'gsi' | null
  const savedTimerRef             = useRef(null);
  const navigate                  = useNavigate();

  const showSavedMsg = (text) => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setSavedMsg(text);
    savedTimerRef.current = setTimeout(() => setSavedMsg(''), 3000);
  };

  useEffect(() => {
    findNearby();
  }, []);

  const findNearby = () => {
    if (!navigator.geolocation) {
      setMsg('ブラウザが位置情報に対応していません。');
      return;
    }
    setSearching(true);
    setLoadingMap(true);
    setMsg('');
    setShelters([]);
    setSelected(null);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const newLoc = { lat: latitude, lng: longitude };
        setLoc(newLoc);
        try {
          const data = await apiGet(
            `/api/shelters/?lat=${latitude}&lng=${longitude}&limit=20`
          );
          /* レスポンス形式: { shelters: [...], total_in_db: N, matched: N } */
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data.shelters)
              ? data.shelters
              : [];
          const src = data.source ?? 'gsi';
          console.log(`[BCP] 取得件数: ${list.length}, ソース: ${src}`);

          setShelters(list);
          setDataSource(src);

          if (list.length > 0) {
            setSelected(list[0]);
          } else {
            setMsg('付近に避難所が見つかりませんでした。半径を広げて再度お試しください。');
          }
        } catch (err) {
          console.error('避難所取得エラー:', err);
          setMsg('避難所の取得に失敗しました。ネットワーク接続を確認してください。');
        } finally {
          setSearching(false);
          setLoadingMap(false);
        }
      },
      (err) => {
        setMsg('位置情報の取得に失敗しました。\n' + err.message);
        setSearching(false);
        setLoadingMap(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (!selected || !loc) {
      alert('避難所を選択してください。');
      return;
    }
    try {
      await apiPostJSON('/api/employee-bcp/', {
        latitude:           loc.lat,
        longitude:          loc.lng,
        nearest_shelter_id: selected.id,
        shelter_name:       selected.name,
        shelter_address:    selected.address || '',
        shelter_lat:        selected.latitude,
        shelter_lng:        selected.longitude,
      });
      showSavedMsg(`「${selected.name}」を保存しました。`);
    } catch (err) {
      console.error('保存エラー:', err);
      let detail = '';
      try { detail = JSON.parse(err.message); } catch (_) { detail = err.message; }
      const detailStr = typeof detail === 'object' ? JSON.stringify(detail) : detail;
      alert(`保存に失敗しました。\n${detailStr || 'ネットワーク接続を確認してください。'}`);
    }
  };

  return (
    <div className="bcp-page">
      <h2>現在地周辺の避難所</h2>
      <p>現在地の位置情報を使って、近くの避難所を表示します。</p>
      
      <div style={{ marginBottom: 14 }}>
        <button onClick={findNearby} className="bcp-btn-primary" disabled={searching}>
          {searching ? '取得中...' : '🔄 現在地を再取得する'} 
        </button>
      </div>
      
      {msg && <div className="error" style={{ whiteSpace: 'pre-line' }}>{msg}</div>}

      {loadingMap && !loc && (
        <p style={{ color: '#555', fontSize: 13 }}>位置情報を取得しています...</p>
      )}

      {/* データソースラベル */}
      {dataSource && shelters.length > 0 && (
        <div style={{
          fontSize: 11, color: dataSource === 'gsi' ? '#1e7bd4' : '#28a745',
          marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {dataSource === 'gsi'
            ? '📡 国土地理院APIよりリアルタイム取得'
            : '🗄️ データベースより取得'}
        </div>
      )}

      {loc && (
        <div className="map-and-list">
          <div className="map-box">
            <div className="bcp-map-wrap">
              <MapContainer
                center={[loc.lat, loc.lng]}
                zoom={14}
                style={{ height: '100%', width: '100%' }}
              >
                <MapMover center={[loc.lat, loc.lng]} />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[loc.lat, loc.lng]} icon={currentLocationIcon}>
                  <Popup>📍 現在地</Popup>
                </Marker>
                {shelters.map(s => (
                  <Marker
                    key={s.id}
                    position={[s.latitude, s.longitude]}
                    eventHandlers={{ click: () => setSelected(s) }}
                  >
                    <Popup>
                      <b>{s.name}</b><br />
                      {s.address}<br />
                      距離: {s.distance_km} km
                    </Popup>
                  </Marker>
                ))}
                {selected && (
                  <Polyline
                    positions={[
                      [loc.lat, loc.lng],
                      [selected.latitude, selected.longitude],
                    ]}
                  />
                )}
              </MapContainer>
            </div>
          </div>

          <div className="list-box">
            {/* 保存エリアをリスト上部に固定 */}
            <div className="list-box-header">
              <p className="selected-info">
                選択中の避難所:{' '}
                {selected
                  ? <b>{selected.name}</b>
                  : <span style={{ color: '#999' }}>なし</span>
                }
              </p>
              {/*}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={handleSave}
                  className="bcp-btn-save"
                  disabled={!selected}
                >
                  保存
                </button>
                {savedMsg && <span className="saved-message">{savedMsg}</span>}
              </div>
              */}
            </div>

            <div className="list-box-scroll">
              {shelters.length === 0 && !searching && (
                <p style={{ padding: '12px', color: '#888', fontSize: 13 }}>
                  候補がありません。
                </p>
              )}
              <ul>
                {shelters.map(s => (
                  <li
                    key={s.id}
                    className={selected && selected.id === s.id ? 'selected' : ''}
                    onClick={() => setSelected(s)}
                  >
                    <div><b>{s.name}</b> — {s.distance_km} km</div>
                    <div style={{ fontSize: 12, color: '#555' }}>{s.address}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="bcp-btn-group" style={{ marginTop: 20 }}>
        <button className="bcp-btn-back" onClick={() => navigate('/bcp')}>
          戻る
        </button>
      </div>
    </div>
  );
};

export default BcpNearbyShelter;
